import { z } from 'zod';
import { normalizarTelefono } from '../texto';

/**
 * Quién trabaja en el condominio.
 *
 * No es el padrón: aquí van los guardias, el jefe de seguridad y quien
 * administra. Los residentes tienen su propia alta en el padrón, con su
 * vivienda y su parentesco, y mezclarlos sería confundir a quien vive aquí con
 * quien trabaja aquí.
 */

/** Los roles que esta pantalla puede dar de alta. */
/**
 * Un teléfono, guardado siempre en la misma forma.
 *
 * Transforma en vez de solo validar: lo que se guarda es la forma canónica, no
 * lo que se tecleó. Si no, `5551234567` y `+52 55 5123 4567` vuelven a ser dos
 * filas distintas y el índice único no lo impide — para Postgres son dos
 * cadenas diferentes. Ver `normalizarTelefono`.
 */
export const telefonoSchema = z
  .string()
  .trim()
  .transform((v, ctx) => {
    const limpio = normalizarTelefono(v);
    if (!limpio) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Ese teléfono no se entiende. Escribe los diez dígitos.',
      });
      return z.NEVER;
    }
    return limpio;
  });

export const ROLES_PERSONAL = ['ADMIN_CONDOMINIO', 'JEFE_SEGURIDAD', 'GUARDIA'] as const;
export type RolPersonal = (typeof ROLES_PERSONAL)[number];

/**
 * Un guardia entra con su teléfono y los demás con su correo.
 *
 * No es una preferencia: en la caseta se teclea en una tablet con guantes y a
 * las tres de la mañana. Un correo es más largo, tiene arroba y se escribe mal;
 * el teléfono son diez dígitos que la persona ya se sabe. Por eso el correo es
 * opcional y el teléfono también, pero **hace falta al menos uno**: sin ninguno
 * la cuenta no tendría forma de entrar.
 */
const identificable = <T extends { email?: unknown; telefono?: unknown }>(d: T) =>
  Boolean(d.email) || Boolean(d.telefono);

export const crearUsuarioSchema = z
  .object({
    nombre: z.string().trim().min(1, 'La persona necesita nombre').max(60),
    apellidoPaterno: z.string().trim().max(60).nullish(),
    apellidoMaterno: z.string().trim().max(60).nullish(),
    email: z.string().trim().toLowerCase().email('Correo inválido').nullish(),
    telefono: telefonoSchema.nullish(),
    rol: z.enum(ROLES_PERSONAL),
    /**
     * Las rejas que atiende, **solo para un guardia**.
     *
     * De aquí sale lo que puede ver y accionar. Administración y jefe de
     * seguridad ven el condominio entero, así que para ellos esta lista no
     * significa nada y se ignora.
     */
    puntosAcceso: z.array(z.string().cuid()).default([]),
    /**
     * La contraseña con la que entra. Se guarda cifrada y no se vuelve a leer.
     *
     * Ocho es poco para una cuenta de banco y suficiente para una tablet que
     * vive dentro de una caseta cerrada: exigir doce con símbolos en un teclado
     * táctil termina en un papel pegado al monitor, que es peor.
     */
    password: z.string().min(8, 'Al menos 8 caracteres').max(72),
  })
  .refine(identificable, {
    message: 'Hace falta un correo o un teléfono: es con lo que entra.',
    path: ['telefono'],
  });

export const actualizarUsuarioSchema = z.object({
  nombre: z.string().trim().min(1).max(60).optional(),
  apellidoPaterno: z.string().trim().max(60).nullish(),
  apellidoMaterno: z.string().trim().max(60).nullish(),
  email: z.string().trim().toLowerCase().email('Correo inválido').nullish(),
  telefono: telefonoSchema.nullish(),
  rol: z.enum(ROLES_PERSONAL).optional(),
  puntosAcceso: z.array(z.string().cuid()).optional(),
  /** Ausente = no se toca. Cambiarla es una acción aparte y deliberada. */
  password: z.string().min(8, 'Al menos 8 caracteres').max(72).optional(),
});

export type CrearUsuario = z.infer<typeof crearUsuarioSchema>;
export type ActualizarUsuario = z.infer<typeof actualizarUsuarioSchema>;
